import { useMemo } from 'react'
import {
  getImage,
  getNestedSubelementsAndFloatingElementsByType,
  getNestedSubelementsByType,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useSharedBrowserStateContext } from '../../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useR2StateContext } from '../../../../../providers/r2Provider/R2Context'

/**
 * @typedef {Object} PersonNameGroup
 * @property {string} id - The record element the names belong to, or `<ark>-floaters`
 * @property {Array<Object>} persons - SLS person elements in that group, in image order
 */

/**
 * @typedef {Object} ImageNameGroup
 * @property {string} ark - The image ark the group belongs to
 * @property {number} imageNumber - 1-based image number, used for the section heading
 * @property {Array<PersonNameGroup>} personGroups - The same names, split by the group they are already in
 */

/**
 * Splits an image's names into the groups/records they already belong to.
 *
 * Records are the only grouping SLS gives us, so every name in no record — a floater — shares one
 * trailing group. Floaters reach an image through `floatingElements` as well as `subElements`, and
 * `getNestedSubelementsAndFloatingElementsByType` is the only helper that reads both.
 *
 * @param {Object} params
 * @param {Object} [params.image] - The SLS image element, absent when the image is not in state
 * @param {string} params.ark - The image ark; names anchored to another image are dropped
 * @param {Set<string>} params.excludedIdSet - Person ids to omit
 * @returns {Array<PersonNameGroup>} Groups holding at least one name, in image order
 */
const buildPersonGroups = ({ image, ark, excludedIdSet }) => {
  const groups = []
  // Claiming a name here keeps it from rendering twice under a duplicate key, and lets the floater
  // pass below take every name the records did not.
  const taken = new Set()

  const addGroup = (id, persons) => {
    const selectable = []
    persons.forEach((person) => {
      if (person.partitionKey !== ark || excludedIdSet.has(person.id) || taken.has(person.id)) return
      taken.add(person.id)
      selectable.push(person)
    })
    if (selectable.length) groups.push({ id, persons: selectable })
  }

  getNestedSubelementsByType(image, 'RECORD').forEach((record) =>
    addGroup(record.id, getNestedSubelementsByType(record, 'PERSON'))
  )

  addGroup(`${ark}-floaters`, getNestedSubelementsAndFloatingElementsByType(image, 'PERSON'))

  return groups
}

/**
 * @typedef {Object} UsePersonsByImageResult
 * @property {Array<ImageNameGroup>} imageGroups - One group per image, in ribbon order
 */

/**
 * Groups the names available for selection by the image they sit on.
 *
 * Images with no names are still returned as an empty group so the caller can render
 * an empty state rather than silently dropping the section.
 *
 * Shared with {@link ../../linkNamesPanel/components/GroupedPersonCheckboxes} in intent: both group
 * names per image and derive the displayed image number from the browser ribbon order.
 *
 * @param {Object} params
 * @param {Array<string>} [params.excludePersonIds=[]] - Person ids to omit (e.g. the viewed person)
 * @returns {UsePersonsByImageResult}
 */
export const usePersonsByImage = ({ excludePersonIds = [] } = {}) => {
  const { images, currentImageArk } = useSlsStateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const { splitScreen, splitScreenImageGrid, referenceFocusedIndex } = useR2StateContext()

  const excludedIdSet = useMemo(() => new Set(excludePersonIds), [excludePersonIds])

  // The ribbon order is the source of truth for the image number shown to the patron.
  const arkOrder = useMemo(() => {
    if (!thumbnails?.length) return [currentImageArk].filter(Boolean)
    return thumbnails.map((thumbnail) => thumbnail.ark)
  }, [thumbnails, currentImageArk])

  // The reference viewer only has a focused image once it leaves grid mode; in grid mode
  // `referenceFocusedIndex` is still its default and would point at an image nobody has chosen.
  // Same guard as R2Image, SideSheet and useImageHighlightInitialization.
  const referenceArk = splitScreen && !splitScreenImageGrid ? thumbnails?.[referenceFocusedIndex]?.ark : undefined

  const imageGroups = useMemo(() => {
    // Listed in ribbon order so the image numbers a patron reads run in the order the sheet shows.
    const arksToShow = [...new Set([currentImageArk, referenceArk].filter(Boolean))].sort(
      (a, b) => arkOrder.indexOf(a) - arkOrder.indexOf(b)
    )

    return arksToShow.reduce((groups, ark, index) => {
      const image = getImage(images, ark)
      // Only images loaded into SLS state can contribute names, but keep the current image so the
      // sheet always has a section to render.
      if (!image && ark !== currentImageArk) return groups

      const personGroups = buildPersonGroups({ image, ark, excludedIdSet })

      groups.push({
        ark,
        imageNumber: arkOrder.indexOf(ark) + 1 || index + 1,
        personGroups,
      })
      return groups
    }, [])
  }, [arkOrder, currentImageArk, referenceArk, images, excludedIdSet])

  return { imageGroups }
}

export default usePersonsByImage
